import { computed, reactive } from 'vue'
import { useFeatureFlags } from '@/Hooks/Company/useFeatureFlags'
import { useAuth } from '@/Hooks/Company/useAuth'
import { usePermissions } from '@/Hooks/Company/usePermissions'
import { usePhotographerBrandingName } from '@/Hooks/Company/ZillowMediaExclusives/ZillowPhotographerBranding/usePhotographerBrandingName.js'

const { isAryeoLiteUser } = useAuth()

export default function useZillowMediaExclusivesNavigation() {
  const { hasFeatureFlag } = useFeatureFlags()
  const { hasPermission } = usePermissions()
  const { photographerBrandingName } = usePhotographerBrandingName()

  const canManageZillowMediaExclusives = hasPermission('zillow_media_exclusives_manage')
  const isRebrand = computed(() => hasFeatureFlag('zillow_integrations_rebrand'))

  const routePrefix = computed(() =>
    isRebrand.value
      ? 'web.company.zillow-integrations.zillow-media-advantage'
      : 'web.company.zillow-media-exclusives'
  )

  const integrationsRoutePrefix = computed(() =>
    isRebrand.value
      ? 'web.company.zillow-integrations'
      : 'web.company.zillow-media-exclusives'
  )

  const routeName = (suffix) => `${routePrefix.value}.${suffix}`
  const integrationsRouteName = (suffix) => `${integrationsRoutePrefix.value}.${suffix}`

  const photographerBrandingCard = computed(() => ({
    show: true,
    comingSoon: false,
    name: isRebrand.value ? 'Zillow Media Advantage' : photographerBrandingName.value,
    description: isRebrand.value
      ? 'Boost your brand — get high-resolution photos, your company logo, and an enhanced shopping experience in front of every Zillow shopper.'
      : "Boost your brand. High resolution photos and your company's name and logo will be displayed in Zillow's photo viewer.",
    routeIsInertia: true,
    targetRouteName: canManageZillowMediaExclusives
      ? routeName('zillow-photographer-branding.show')
      : null,
    image:
      'https://aryeo.sfo2.cdn.digitaloceanspaces.com/aryeo_media/files/zillow-media-exclusives/photographer-branding-hd-photos.png',
    imageObjectFit: 'contain',
    routeNamesToCheckIfCurrent: [
      'web.company.zillow-media-exclusives.zillow-photographer-branding.*',
      'web.company.zillow-integrations.zillow-media-advantage.zillow-photographer-branding.*',
    ],
    integrationProvider: 'ZILLOW_PHOTOGRAPHER_BRANDING',
    shouldShowBadge: true,
    requiresProAccount: false,
  }))

  const showcaseSelectCard = computed(() => ({
    show: true,
    comingSoon: false,
    name: 'Showcase Select',
    description:
      'Get more business by making your company discoverable and eligible for direct Zillow Showcase media booking.',
    routeIsInertia: true,
    targetRouteName: canManageZillowMediaExclusives
      ? integrationsRouteName('showcase-select.show')
      : null,
    image:
      'https://aryeo.sfo2.cdn.digitaloceanspaces.com/aryeo_media/files/zillow-media-exclusives/showcase-select.png',
    routeNamesToCheckIfCurrent: [
      'web.company.zillow-media-exclusives.showcase-select.*',
      'web.company.zillow-integrations.showcase-select.*',
    ],
    integrationProvider: 'SHOWCASE',
    shouldShowBadge: true,
    requiresProAccount: true,
  }))

  const virtualStagingCard = computed(() => ({
    show: true,
    comingSoon: false,
    name: 'Virtual Staging AI',
    description:
      'Enhance your listing photos for free with AI-powered virtual staging and day to dusk transformations.',
    routeIsInertia: true,
    targetRouteName: canManageZillowMediaExclusives
      ? (isRebrand.value ? 'web.company.app-store.virtual-staging-ai.show' : routeName('virtual-staging-ai.show'))
      : null,
    image:
      'https://aryeo.sfo2.cdn.digitaloceanspaces.com/aryeo_media/vsai_examples/staging-examples/bedroom-modern.webp',
    imageObjectFit: 'contain',
    routeNamesToCheckIfCurrent: [
      'web.company.zillow-media-exclusives.virtual-staging-ai.*',
      'web.company.app-store.virtual-staging-ai.*',
    ],
    integrationProvider: 'VIRTUAL_STAGING_AI',
    shouldShowBadge: true,
    requiresProAccount: true,
  }))

  const zillowRentalsCard = computed(() => ({
    show: !isAryeoLiteUser.value && hasFeatureFlag('zillow_rentals'),
    comingSoon: false,
    name: 'Zillow Rentals',
    description:
      'Get recommended to Zillow Rental Partners by joining a curated list of media ' +
      'providers who can capture Zillow 3D Home tours and interactive floor plans.',
    routeIsInertia: true,
    targetRouteName: canManageZillowMediaExclusives
      ? integrationsRouteName('zillow-rentals.show')
      : null,
    image:
      'https://aryeo.sfo2.cdn.digitaloceanspaces.com/aryeo_media/files/zillow-media-exclusives/zillow-rentals.png',
    imageObjectFit: 'contain',
    routeNamesToCheckIfCurrent: [
      'web.company.zillow-media-exclusives.zillow-rentals.*',
      'web.company.zillow-integrations.zillow-rentals.*',
    ],
    integrationProvider: 'ZILLOW_RENTALS',
    shouldShowBadge: true,
    requiresProAccount: false,
  }))

  const streetEasyCard = computed(() => ({
    show: !isAryeoLiteUser.value && hasFeatureFlag('zillow_streeteasy'),
    comingSoon: false,
    name: 'StreetEasy',
    description:
      'Get recommended to StreetEasy partners by joining a curated list of media ' +
      'providers who can capture Zillow 3D Home tours and interactive floor plans.',
    routeIsInertia: true,
    targetRouteName: canManageZillowMediaExclusives
      ? integrationsRouteName('streeteasy.show')
      : null,
    image:
      'https://aryeo.sfo2.cdn.digitaloceanspaces.com/aryeo_media/app_store_tiles/streeteasy-brand-tile.png',
    imageObjectFit: 'contain',
    routeNamesToCheckIfCurrent: [
      'web.company.zillow-media-exclusives.streeteasy.*',
      'web.company.zillow-integrations.streeteasy.*',
    ],
    integrationProvider: 'ZILLOW_STREETEASY',
    shouldShowBadge: true,
    requiresProAccount: false,
  }))

  const zillow3dHomeCard = computed(() => ({
    show: true,
    comingSoon: !hasFeatureFlag('zillow_3d_home'),
    name: 'Zillow 3D Home',
    description:
      'Streamline your workflow and sync tours directly to your listings with the 3D Home Integration.',
    routeIsInertia: true,
    targetRouteName: canManageZillowMediaExclusives
      ? integrationsRouteName('zillow-3d-home.show')
      : null,
    image:
      'https://aryeo.sfo2.cdn.digitaloceanspaces.com/aryeo_media/files/zillow-media-exclusives/zillow-3d-home.png',
    routeNamesToCheckIfCurrent: [
      'web.company.zillow-media-exclusives.zillow-3d-home.*',
      'web.company.zillow-integrations.zillow-3d-home.*',
    ],
    integrationProvider: 'ZILLOW_3D_HOME',
    shouldShowBadge: true,
    requiresProAccount: true,
  }))

  const moreExclusivesCard = {
    show: true,
    comingSoon: true,
    name: 'More Exclusives',
    description:
      'Your experience is about to get even better! New features and enhancements are coming soon!',
    image:
      'https://aryeo.sfo2.cdn.digitaloceanspaces.com/aryeo_media/files/zillow-media-exclusives/more-media-exclusives.png',
    imageObjectFit: 'contain',
    shouldShowBadge: true,
    requiresProAccount: false,
  }

  const zillowProfileCard = computed(() => ({
    show: true,
    name: 'Zillow Profile',
    description: isRebrand.value
      ? 'Create and manage your company profile that will be used across the Zillow integrations you have enabled for your company.'
      : 'Create and manage your company profile that will be used across the Zillow media exclusives you have enabled for your company.',
    routeIsInertia: true,
    targetRouteName: canManageZillowMediaExclusives
      ? integrationsRouteName('profile.edit')
      : null,
    routeNamesToCheckIfCurrent: [
      'web.company.zillow-media-exclusives.profile.*',
      'web.company.zillow-integrations.profile.*',
    ],
    shouldShowBadge: false,
  }))

  const settingsSection = computed(() => ({
    show: true,
    name: 'Settings',
    alwaysShowHeader: true,
    description: '',
    children: [zillowProfileCard.value],
  }))

  const sections = computed(() => {
    if (isRebrand.value) {
      return [
        {
          show: true,
          name: 'Zillow Media Advantage',
          tag: 'Featured',
          description: '',
          children: [photographerBrandingCard.value],
        },
        {
          show: true,
          name: 'Additional Zillow features',
          description: '',
          children: [
            showcaseSelectCard.value,
            zillowRentalsCard.value,
            streetEasyCard.value,
            zillow3dHomeCard.value,
            moreExclusivesCard,
          ],
        },
        settingsSection.value,
      ]
    }

    return [
      {
        show: true,
        name: 'Media Exclusives',
        description: '',
        children: [
          photographerBrandingCard.value,
          showcaseSelectCard.value,
          virtualStagingCard.value,
          zillowRentalsCard.value,
          streetEasyCard.value,
          zillow3dHomeCard.value,
          moreExclusivesCard,
        ],
      },
      settingsSection.value,
    ]
  })

  const sidebarSections = computed(() => {
    if (!isRebrand.value) {
      return sections.value
    }

    const grouped = sections.value.filter((s) => s.show && !s.alwaysShowHeader)
    const pinned = sections.value.filter((s) => s.show && s.alwaysShowHeader)
    const flatChildren = grouped.flatMap((s) => s.children)

    return [
      ...(flatChildren.length ? [{ show: true, name: undefined, children: flatChildren }] : []),
      ...pinned,
    ]
  })

  return reactive({
    overviewTitle: computed(() => isRebrand.value ? 'Zillow Integrations' : 'Zillow Media Exclusives'),
    overviewRouteName: computed(() =>
      isRebrand.value
        ? 'web.company.zillow-integrations.index'
        : 'web.company.zillow-media-exclusives.index'
    ),
    sections,
    sidebarSections,
  })
}
